/**
 * AssignCMTMDialog — AE picks a Campaign Manager and/or Talent Manager.
 * Reads /api/users/managers?type=campaign|talent (existing endpoint).
 * Writes via POST /api/ae/campaigns/{id}/assign.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Briefcase, UserCog, Check } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AssignCMTMDialog = ({ campaign, kind, onClose, onAssigned }) => {
  // kind: 'cm' | 'tm'
  const [list, setList] = useState([]);
  const [workload, setWorkload] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!campaign) return;
    (async () => {
      try {
        const type = kind === 'cm' ? 'campaign' : 'talent';
        const { data } = await axios.get(`${API_URL}/api/users/managers?type=${type}`, { withCredentials: true });
        setList(data || []);
        if (kind === 'cm') {
          // /users/cm-workload is admin+AE only
          try {
            const { data: w } = await axios.get(`${API_URL}/api/users/cm-workload`, { withCredentials: true });
            const map = {};
            (w || []).forEach((x) => { map[x.id] = x; });
            setWorkload(map);
          } catch (_) { /* ignore */ }
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load list');
      }
    })();
  }, [campaign, kind]);

  const assign = async (manager) => {
    setSaving(true);
    try {
      const payload = kind === 'cm'
        ? { campaign_manager_id: manager.id }
        : { talent_manager_id: manager.id };
      await axios.post(
        `${API_URL}/api/ae/campaigns/${campaign.id}/assign`,
        payload,
        { withCredentials: true },
      );
      toast.success(`Assigned ${manager.name}`);
      onAssigned?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Assignment failed');
    } finally {
      setSaving(false);
    }
  };

  const title = kind === 'cm' ? 'Assign Campaign Manager' : 'Assign Talent Manager';
  const Icon = kind === 'cm' ? Briefcase : UserCog;

  return (
    <Dialog open={!!campaign} onOpenChange={(o) => { if (!saving && !o) onClose?.(); }}>
      <DialogContent className="bg-white dark:bg-[#0A0B0D] border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white max-w-lg" data-testid="ae-assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Icon size={18} className="text-[#FF451A]" /> {title}
          </DialogTitle>
          <DialogDescription className="text-xs text-[#6B6B6B] dark:text-zinc-500">
            Pick a manager to operate <span className="text-zinc-300">{campaign?.campaign_name}</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1 mt-2 max-h-96 overflow-auto" data-testid="ae-assign-list">
          {list.length === 0 ? (
            <p className="text-sm text-[#6B6B6B] dark:text-zinc-500 p-4 text-center">No managers available.</p>
          ) : list.map((m) => {
            const load = kind === 'cm'
              ? `${workload[m.id]?.active_campaigns ?? 0} active campaign${(workload[m.id]?.active_campaigns ?? 0) === 1 ? '' : 's'}`
              : `roster size: ${m.tags?.length ?? '—'}`;
            return (
              <button
                key={m.id}
                onClick={() => assign(m)}
                disabled={saving}
                className="w-full flex items-center justify-between p-3 rounded-lg bg-white/5 border border-black/[0.10] dark:border-white/10 hover:bg-[#FF451A]/10 hover:border-[#FF451A]/40 transition-colors text-left disabled:opacity-50"
                data-testid={`ae-assign-pick-${m.id}`}
              >
                <div>
                  <div className="text-sm font-medium text-[#0F0F0F] dark:text-white">{m.name}</div>
                  <div className="text-[11px] text-[#6B6B6B] dark:text-zinc-500">{m.email} · {load}</div>
                </div>
                <Check size={14} className="text-[#6B6B6B] dark:text-zinc-500" />
              </button>
            );
          })}
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={() => onClose?.()} disabled={saving} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
